﻿import {
	expressQuerySelector,
	expressQuerySelectorAll,
	expressAddClass,
	expressRemoveClass,
	hide,
	show,
	isElementVisible
} from "../common/html";
import {initForm} from "./form";
import {subscribeAsync, unSubscribeAsync, getPersonIdAsync} from "../services-modules/account-service";
import {getParameter} from "../utils_modules/querystring";

export interface INewsletterSubscribeDependencies {
	subscribeAsync: (email: string, appId: string, posId: string, lang: string, source?: string) => Promise<void>;
}

export interface INewsletterUnsubscribeDependencies {
	getPersonIdAsync: (email: string, posId: string) => Promise<any>;
	unSubscribeAsync: (personId: string, appId: string, posId: string) => Promise<void>;
}

export function createNewsletterSubscribeAndUnsubscribe() {
	const subscriptionForms = expressQuerySelectorAll<HTMLElement>(document, ".technical-newsletter-subscribe");
	const unSubscriptionForms = expressQuerySelectorAll<HTMLElement>(document, ".technical-newsletter-unsubscribe");

	if (subscriptionForms && subscriptionForms.length > 0) {
		let ioSubscribe: IntersectionObserver;
		if (('InterSectionObserver' in window || 'IntersectionObserverEntry' in window && 'intersectionRatio' in (window as any).IntersectionObserverEntry.prototype)) {
			ioSubscribe = new IntersectionObserver((entries, observer) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							createNewsletterSubscribe(entry.target as HTMLElement,
								{
									subscribeAsync
								});
							observer.unobserve(entry.target);
						}
					});
				},
				{rootMargin: "0px 0px 0px 0px"}
			);
			subscriptionForms.forEach(el => ioSubscribe.observe(el));
		} else {
			subscriptionForms.forEach(formEL => {
				createNewsletterSubscribe(
					formEL,
					{
						subscribeAsync
					});
			});
		}
	}

	if (unSubscriptionForms && unSubscriptionForms.length > 0) {
		let ioUnSubscribe: IntersectionObserver;
		if (('InterSectionObserver' in window || 'IntersectionObserverEntry' in window && 'intersectionRatio' in (window as any).IntersectionObserverEntry.prototype)) {
			ioUnSubscribe = new IntersectionObserver((entries, observer) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							createNewsletterUnsubscribe(entry.target as HTMLElement,
								{
									getPersonIdAsync,
									unSubscribeAsync
								});
							observer.unobserve(entry.target);
						}
					});
				},
				{rootMargin: "0px 0px 0px 0px"}
			);
			unSubscriptionForms.forEach(el => ioUnSubscribe.observe(el));
		} else {
			unSubscriptionForms.forEach(formEL => {
				createNewsletterUnsubscribe(
					formEL,
					{
						getPersonIdAsync,
						unSubscribeAsync
					});
			});
		}
	}
}

export function createNewsletterSubscribe(containerEl: HTMLElement, deps: INewsletterSubscribeDependencies) {
	const formEl = expressQuerySelector<HTMLFormElement>(containerEl, 'form', true);
	if (!isElementVisible(formEl)) return;
	const emailEl = expressQuerySelector<HTMLInputElement>(formEl, '.technical-subscribe-email', true);
	const emptyGuid = '00000000-0000-0000-0000-000000000000';
	const appId = containerEl.dataset.appid || emptyGuid;
	const posId = containerEl.dataset.posid || emptyGuid;
	const lang = containerEl.dataset.lang || "";

	const {subscribeAsync} = deps;
	emailEl.setAttribute('required', 'required');
	emailEl.setAttribute('emailRFC822', 'true');
	let source = formEl.getAttribute("source") || undefined;
	!source && (source = emailEl.dataset.sourceid);

	const subscribeForm = initForm(
		formEl,
		{
			onSubmitForm: () => {
				return subscribeAsync(emailEl.value, appId, posId, lang, source)
					.then(
						() => {
							expressAddClass(containerEl, "m-newsletter--success");
						}, (e) => {
							expressRemoveClass(containerEl, "m-newsletter--success");
						}
					);
			},
			buttonSelector: ".technical-subscribe-button",
			submitOnEnter: true
		}
	);

	return {dispose: subscribeForm.dispose};

}

export function createNewsletterUnsubscribe(containerEl: HTMLElement, deps: INewsletterUnsubscribeDependencies) {
	const formEl = expressQuerySelector<HTMLFormElement>(containerEl, 'form', true);
	if (!isElementVisible(formEl)) return;
	const successMessageEl = expressQuerySelector<HTMLFormElement>(containerEl, '.technical-success-message', false);
	const errorMessageEl = expressQuerySelector<HTMLFormElement>(containerEl, '.technical-error-message', false);
	const emailEl = expressQuerySelector<HTMLInputElement>(formEl, '.technical-unsubscribe-mail', false);
	const checkBoxEl = expressQuerySelector<HTMLInputElement>(formEl, '#newsletter', false);
	const {getPersonIdAsync, unSubscribeAsync} = deps;
	const emptyGuid = '00000000-0000-0000-0000-000000000000';
	const appId = containerEl.dataset.appid || emptyGuid;
	const posId = containerEl.dataset.posid || emptyGuid;

	const showSuccessMessage = () => {
		successMessageEl && expressRemoveClass(successMessageEl, "u-hide");
		errorMessageEl && expressAddClass(errorMessageEl, "u-hide");
		hide(formEl);
		expressAddClass(containerEl, "m-newsletter--success");
	};

	const showErrorMessage = () => {
		successMessageEl && expressAddClass(successMessageEl, "u-hide");
		errorMessageEl && expressRemoveClass(errorMessageEl, "u-hide");
		show(formEl);
	};

	emailEl && emailEl.setAttribute('required', 'required');
	if (checkBoxEl) checkBoxEl.setAttribute('required', 'required');

	const unsubscribeForm = initForm(
		formEl,
		{
			onSubmitForm: () => {
				if (emailEl !== null) {
					return getPersonIdAsync(emailEl.value, posId).then(res => {
						if (res.GetPersonForEmailResult !== emptyGuid) {
							unSubscribeAsync(res.GetPersonForEmailResult, appId, posId)
								.then(
									() => {
										showSuccessMessage();
									}, (e) => {
										showErrorMessage();
									}
								);
						} else {
							showSuccessMessage();
						}
					});
				} else {
					return unSubscribeAsync(getParameter("personid"), appId, posId)
						.then(
							() => {
								showSuccessMessage();
							}, (e) => {
								showErrorMessage();
							}
						);
				}
			},
			buttonSelector: ".technical-unsubscribe-button",
			submitOnEnter: true
		}
	);

	return {dispose: unsubscribeForm.dispose};
}
